<template>

<app-content :loading="is.loading">

	<app-content-head :title="title" :subtitle="(item.page) ? item.page.name : false" :back="$route.meta.back">

		<app-content-head-actions-item :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
		<app-content-head-actions-item :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />
		
		<app-content-head-actions-item v-if="canDecide" :divide="true" text="Accept" icon="approve" :success="true" :disabled="isDirty" v-on:click="onAcceptClick" />
		<app-content-head-actions-item v-if="canReview" text="Review" icon="timer" :disabled="isDirty" v-on:click="onReviewClick" />
		<app-content-head-actions-item v-if="canDecide" text="Reject" icon="reject.thumb" :danger="true" :disabled="isDirty" v-on:click="onRejectClick" />

	</app-content-head>

	<app-content-body v-if="is.initialised">

		<app-content-summary>

			<app-content-summary-item label="Status" :tag="$constants.registration.application.statusName[item.status]" :theme="$constants.registration.application.statusTheme[item.status]" />
			
			<app-content-summary-item label="Reference" :value="item.reference" />

			<app-content-summary-item label="Name" :value="item.name" />
			
			<app-content-summary-item label="E-mail" :grow="true" :value="item.email" />
			
			<app-content-summary-item label="Submitted On" :value="item.date.submitted | localDate('MM/DD/YYYY h:mma')" />
			
			<app-content-summary-item v-if="!canDecide" label="Decision On" :value="item.date.decided | localDate('MM/DD/YYYY h:mma')" />

			<app-content-summary-item v-if="item.sale" label="Conversion On" :value="item.sale.date.made | localDate('MM/DD/YYYY h:mma')" />

			<app-content-summary-item v-if="item.sale" label="Sale Conversion" :value="'#' + item.sale.reference" :link="linkSale" />

		</app-content-summary>

		<app-content-section v-if="item.linked.total > 1 && !isArchived" icon="warning" :is-slim="true" title="This applicant has multiple applications." :count="item.linked.total" theme="yellow" description="Click to view all applications." :route="linkMultiple" />

		<app-content-section v-if="isArchived && item.linked.id" icon="warning" :is-slim="true" title="This application was archived after another by the same user was accepted." theme="yellow" description="Click to view the accepted application." :route="linkAccepted" />

		<app-content-section v-if="isArchived && !item.linked.id" :no-link="true" icon="warning" :is-slim="true" title="This application was archived after the user registered a badge via other means." theme="yellow" />

		<app-content-box title="Admin" icon="messages">

			<app-input-text v-if="!canDecide && item.feedback" :locked="true" label="Feedback Note" :autogrow="true" placeholder="Enter reason" notes="This note was emailed to the user when the decision was made." v-model="model.feedback" />
			
			<app-input-checklist :columns="1" v-if="isAccepted" label="Approved Badges" notes="These are the badges this accepted application is able to register." v-model="model.badges" :options="$util.options(references.badges)" />
			
			<app-input-text v-if="isAccepted" label="Registration URL" notes="This is the unique URL sent to this specific user so that they can complete their registration." v-model="item.url" :has-copy="true" :has-open="true" v-on:open="onOpenClick" />
			
			<app-input-text label="Private Note" :autogrow="true" placeholder="Enter any notes" notes="This note will never be shared with the user." v-model="model.notes" :validation="$v.model.notes" />

		</app-content-box>

		<app-content-box title="Event Submissions" icon="suggestions" :not-padded="true" v-if="references.suggestions.length">

			<app-table>

				<app-table-head :layout="suggestionLayout">

					<app-table-column text="Status" />
					<app-table-column text="Event" />
					<app-table-column text="Room" />
					<app-table-column text="Space" />
					<app-table-column text="Day" />
					<app-table-column text="Time" />
					<app-table-column text="Duration" />
					<app-table-column border="left" text="Schedule Preferences" />

				</app-table-head>

				<app-table-body>

					<app-table-row :layout="suggestionLayout" v-for="suggestion in references.suggestions" :key="suggestion.id" v-on:click="onSuggestionClick(suggestion.id)">

						<app-table-cell-label :text="$constants.schedule.suggestions.statusName[suggestion.status]" :type="$constants.schedule.suggestions.statusTheme[suggestion.status]" />
						<app-table-cell-text :text="suggestion.name" />
						<app-table-cell-text :text="(suggestion.room) ? suggestion.room.name : false" :can-empty="true" />
						<app-table-cell-text :text="(suggestion.space) ? suggestion.space.name : false" :can-empty="true" />
						<app-table-cell-text :text="(suggestion.day) ? $con.schedule.days[suggestion.day] : false" :can-empty="true" />
						<app-table-cell-text :text="(suggestion.time) ? $con.schedule.times[suggestion.time] : false" :can-empty="true" />
						<app-table-cell-text :text="(suggestion.duration) ? $con.schedule.durations[suggestion.duration] : false" :can-empty="true" />
						<app-table-cell-preferences border="left" :times="suggestion.preferred" />

					</app-table-row>

				</app-table-body>

			</app-table>

		</app-content-box>

		<app-content-box title="Application Fields" icon="info" :not-padded="true">

			<app-table :empty="!$_.keys(item.data).length" emptyMessage="There is no additional information.">

				<app-table-head :layout="infoLayout">

					<app-table-column text="Question" />
					<app-table-column text="Answer" />

				</app-table-head>

				<app-table-body>

					<app-table-row :layout="infoLayout" v-for="(item, id) in item.data" :key="id">

						<app-table-cell-text :text="item.label" />
						<app-table-cell-text :html="lineBreak(item.value)" />

					</app-table-row>

				</app-table-body>

			</app-table>

		</app-content-box>
		
	</app-content-body>

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import nl2br from 'nl2br'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			infoLayout: '200px auto',
			suggestionLayout: '120px 150px 100px 80px 80px 80px 80px auto',
			references: {
				badges: [],
				suggestions: []
			},
			model: {
				id: false,
				feedback: '',
				token: '',
				notes: '',
				badges: []
			}
		}

	},

	validations: {
		model: {
		}
	},

	computed: {
		
		linkAccepted: function() {
		
			return {
				name: 'Convention.Registration.Applications.Edit',
				params: {
					id: this.item.linked.id
				}
			}

		},
		
		linkMultiple: function() {
		
			return {
				name: 'Convention.Registration.Applications',
				params: {
					filter: {
						search: this.item.email
					}
				}
			}

		},

		linkSale: function() {
		
			return (this.item.sale) ? {
				name: 'Convention.Registration.Sales.Edit',
				params: {
					id: this.item.sale.id
				}
			} : false
		
		},

		isArchived: function() {

			return this.$_.contains([
				this.$constants.registration.application.status.archived
			], this.item.status)
		
		},
	
		isAccepted: function() {

			return this.$_.contains([
				this.$constants.registration.application.status.accepted
			], this.item.status)
		
		},
	
		canReview: function() {

			return this.$_.contains([
				this.$constants.registration.application.status.pending
			], this.item.status)
		
		},

		canDecide: function() {

			return this.$_.contains([
				this.$constants.registration.application.status.pending,
				this.$constants.registration.application.status.reviewing
			], this.item.status)
		
		}

	},

	methods: {

		onSuggestionClick: function(id) {
		
			this.$router.push({
				name: 'Convention.Schedule.Suggestions.Edit',
				params: {
					id: id
				}
			})

		},
	
		lineBreak: function(value) {
		
			return nl2br(value)
		
		},
		
		onOpenClick: function() {
		
			window.open(this.item.url, '_blank').focus();

		},

		onAcceptClick: function() {

			this.$router.push({
				name: 'Convention.Registration.Application.Accept'
			})
		
		},

		onRejectClick: function() {

			this.$router.push({
				name: 'Convention.Registration.Application.Reject'
			})
		
		},

		onReviewClick: function() {

			this.$router.push({
				name: 'Convention.Registration.Application.Review'
			})
		
		}
	
	}

}

</script>

<style scoped>

</style>